import React from 'react';
import { dayjs } from 'app/dayjs';
import { Box, Grid } from '@material-ui/core';
import {
  Currency,
  CurrencyVariant,
  Typography,
  TypographyVariant,
} from 'components/core';
import { useBusiness } from 'hooks/business/use-business';
import { useAggregateBalances } from 'hooks/portal-gateway/use-aggregate-balances';
import { useDashboardActivity } from 'hooks/portal-gateway/use-dashboard-activity';
import { Transaction } from 'modules/dashboard/treasure-reserve/transactions/components/transactions-table/transactions-table';
import { Greeting } from 'modules/dashboard/treasure-reserve/overview/components/greeting/greeting';
import { useDividendsAndInterest } from 'hooks/custodian/use-dividends-and-interest';
import { calculateReturns } from './utils/calculate-returns';

const OverviewNumber = ({
  label,
  value,
  showPending,
}: {
  label: string;
  value: number;
  showPending?: boolean;
}) => {
  return (
    <>
      <Typography variant={TypographyVariant.Paragraph2}>{label}</Typography>

      <Typography variant={TypographyVariant.HeaderRegular}>
        {showPending ? (
          'Pending'
        ) : (
          <Currency number={value} variant={CurrencyVariant.Full} />
        )}
      </Typography>
    </>
  );
};

export const BridgeReturnsOverview = () => {
  const { data: business, isLoading: isLoadingBusiness } = useBusiness();
  const { data: aggregateBalances, isLoading: isLoadingAggregateBalances } =
    useAggregateBalances(business?.Id);
  const {
    data: dividendsAndInterest,
    isLoading: isLoadingDividendsAndInterest,
  } = useDividendsAndInterest(business?.Id);

  // TODO: temporarily use transactions data while account balances gets fixed
  const { data: dashboardActivity, isLoading: isLoadingDashboardActivity } =
    useDashboardActivity();
  const pendingTransactions = dashboardActivity?.transactions?.filter(
    (transaction: Transaction) =>
      transaction.status === 'IN_PROGRESS' || transaction.status === 'PENDING',
  );

  const totalBalance = aggregateBalances?.aum?.total?.amount;

  const pendingDeposits = pendingTransactions?.reduce(
    (sum: number, pendingTransaction: Transaction) =>
      sum +
      (pendingTransaction.description.includes('into')
        ? Number(pendingTransaction.amount)
        : 0),
    0,
  );
  const pendingWithdrawals = pendingTransactions?.reduce(
    (sum: number, pendingTransaction: Transaction) =>
      sum +
      (pendingTransaction.description.includes('out')
        ? Number(pendingTransaction.amount)
        : 0),
    0,
  );

  const thisMonth = dayjs().startOf('month');
  const lastMonth = thisMonth.subtract(1, 'month');

  const thisMonthReturnsData = dividendsAndInterest?.find(
    (x: { start_date: string }) =>
      x.start_date === thisMonth.format('YYYY-MM-DD'),
  );
  const lastMonthReturnsData = dividendsAndInterest?.find(
    (x: { start_date: string }) =>
      x.start_date === lastMonth.format('YYYY-MM-DD'),
  );

  const { showPending, returnsGross, returnsNet, returnsFee } =
    calculateReturns(thisMonthReturnsData, lastMonthReturnsData);

  if (
    isLoadingBusiness ||
    isLoadingDashboardActivity ||
    isLoadingDividendsAndInterest ||
    isLoadingAggregateBalances
  ) {
    return null;
  }

  return (
    <>
      <Box mb={6}>
        <Greeting />
      </Box>

      <Grid container spacing={4}>
        <Grid item>
          <Box mr={8}>
            <Typography variant={TypographyVariant.Body}>
              Balance Total
            </Typography>

            <Typography variant={TypographyVariant.LargeHeader}>
              <Currency number={totalBalance} variant={CurrencyVariant.Full} />
            </Typography>
          </Box>
        </Grid>

        <Grid item>
          <OverviewNumber label="Pending Deposits" value={pendingDeposits} />
        </Grid>

        <Grid item>
          <OverviewNumber
            label="Pending Withdrawals"
            value={pendingWithdrawals}
          />
        </Grid>

        <Grid item>
          <OverviewNumber
            label={`${lastMonth.format('MMM')} Return (Gross)`}
            value={returnsGross}
            showPending={showPending}
          />
        </Grid>

        <Grid item>
          <OverviewNumber
            label={`${lastMonth.format('MMM')} Return (Net)`}
            value={returnsNet}
            showPending={showPending}
          />
        </Grid>

        <Grid item>
          <OverviewNumber
            label={`${lastMonth.format('MMM')} Fee`}
            value={returnsFee}
            showPending={showPending}
          />
        </Grid>
      </Grid>
    </>
  );
};
